import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { unique } from 'lib/array/arrayUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import Select from 'components/Common/Form/Input/Select'
import { mediaQueryUp, mediaQueryDown } from 'components/utils/breakpoint'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import ExperienceDateSelect from './ExperienceDateSelect'
import MarkdownRender from 'LuxKit/MarkdownRender'
import InputText from 'LuxKit/Typography/InputText'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'

const BookingDateTimeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    grid-column-gap: ${rem(12)};

    > .full-width {
      grid-column: span 2;
    }
  }
`

const UniqueRegisterContainer = styled.div`
  display: flex;
  align-items: center;
  height: ${rem(40)};
`

const TicketOptionsTitle = styled(BodyText)`
  ${mediaQueryDown.tablet} {
    padding-top: ${rem(16)};
  }
`
interface Props {
  onTimeSelect?: (time: string) => void;
  onDateSelect?: (date: App.ExperienceAvailabilityDate) => void;
  onPickupPointSelect?: (pickupPoint?: App.ExperiencePickupPoint) => void;
  onLocationSelect?: (location?: App.ExperienceRedemptionLocation) => void;
  onLanguageSelect?: (language?: App.ExperienceLanguage) => void;
  onTicketModeSelect?: (ticketMode?: App.ExperienceTicketMode) => void;
  experienceView: App.ExperienceBookingView;
  allDates?: Array<App.ExperienceAvailabilityDate>;
  availableTimes?: Array<App.ExperienceAvailabilityTimeSlot>;
  defaultTimeSlot?: string;
  selectedTimeSlot?: string;
  defaultPickupPointId?: string;
  defaultLanguageId?: string;
  defaultRedemptionLocationId?: string;
  defaultTicketMode?: string;
  selectedDate?: string;
  checkInDate?: string;
  checkOutDate?: string;
  removeLocationLabel?: boolean;
}

function ExperienceTicketInputs(props: Props) {
  const {
    onTimeSelect,
    onDateSelect,
    onPickupPointSelect,
    onLocationSelect,
    onLanguageSelect,
    onTicketModeSelect,
    experienceView,
    allDates,
    availableTimes,
    defaultTimeSlot,
    selectedTimeSlot,
    defaultPickupPointId,
    defaultLanguageId,
    defaultRedemptionLocationId,
    defaultTicketMode,
    selectedDate,
    checkInDate,
    checkOutDate,
    removeLocationLabel = false,
  } = props

  const trackEvent = useTrackExperienceOfferEvent()

  const languages = experienceView.originalExperience?.languages ?? []
  const pickupPoints = experienceView.originalExperience?.pickupPoints ?? []

  const onTimeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    onTimeSelect?.(e.currentTarget.value)
    trackEvent('TIME_SLOT_SELECTED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [onTimeSelect])

  const onPickupPointChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const pickupId = e.currentTarget.value
    const pickupPoint = experienceView.originalExperience.pickupPoints.find(p => p.id === pickupId)
    onPickupPointSelect?.(pickupPoint)
    trackEvent('PICKUP_LOCATION_SELECTED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [onPickupPointSelect, experienceView])

  const onLocationChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const locationId = e.currentTarget.value
    const location = experienceView.originalExperience.redemptionLocations.find(rl => rl.id === locationId)
    onLocationSelect?.(location)
    trackEvent('REDEMPTION_LOCATION_SELECTED')
  // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [onLocationSelect, experienceView])

  const onTicketModeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const ticketModeKey = e.currentTarget.value
    const ticketMode = experienceView.originalExperience.ticketModes?.find(rl => rl.key === ticketModeKey)
    onTicketModeSelect?.(ticketMode)
    trackEvent('TICKET_MODE_SELECTED')
  // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [onLocationSelect, experienceView])

  const onLanguageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const languageId = e.currentTarget.value
    const language = experienceView.originalExperience.languages.find(l => l.id === languageId)
    onLanguageSelect?.(language)
    trackEvent('LANGUAGE_SELECTED')
  // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [onLanguageSelect, experienceView])

  const uniqueTimeSlots = useMemo(() => {
    return unique((availableTimes ?? []).map(slot => slot.time))
  }, [availableTimes])

  useEffect(() => {
    if (uniqueTimeSlots.length === 1) {
      onTimeSelect?.(uniqueTimeSlots[0])
      trackEvent('TIME_SLOT_SELECTED_NON_INTERACTION')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [onTimeSelect, uniqueTimeSlots])

  const availableDates = useMemo(() => allDates?.filter(date => !date.soldOut) ?? [], [allDates])

  const showTimeSlots = experienceView.originalExperience.hasTimeslots && !experienceView.originalExperience.hideTimeSlots

  const redemptionLocation = experienceView.originalExperience?.redemptionLocations
  const showRedemptionLocation = redemptionLocation.length > 0
  const showReadOnlyRedemptionLocation = redemptionLocation.length === 1

  const pickupRequired = !experienceView.originalExperience?.pickupOptional
  const showPickupPoint = pickupPoints.length > 0
  const showReadOnlyPickupPoint = pickupPoints.length === 1 && pickupRequired
  const hasAnyInputs = languages.length > 0 || showPickupPoint || experienceView.originalExperience.hasCalendar || experienceView.originalExperience.hasTimeslots
  return (<BookingDateTimeContainer>
    {hasAnyInputs && <BodyText className="full-width" variant="large">
      To check availability and prices, please let us know the following:
    </BodyText>}
    {!showPickupPoint && !showRedemptionLocation && experienceView.originalExperience.copy.whereText &&
      <div className="full-width">
        <BodyText variant="medium" weight="bold">Where</BodyText>
        <MarkdownRender content={experienceView.originalExperience.copy.whereText} />
      </div>}

    {showPickupPoint && <>
      {!showReadOnlyPickupPoint && <Select
        label="Pickup point"
        placeholder="Select a pickup point"
        name="pickupPoint"
        className="full-width"
        onChange={onPickupPointChange}
        defaultValue={defaultPickupPointId ?? ((pickupPoints.length === 1 && pickupRequired) ? pickupPoints[0].id : undefined)}
        required={pickupRequired}
        noValidationSpacing
      >
        {!pickupRequired && <option>-</option>}
        {pickupPoints.map(point => <option key={point.id} value={point.id}>{point.name}</option>)}
      </Select>}
      {showReadOnlyPickupPoint && <div className="full-width">
        <InputText variant="text-regular">Pickup point</InputText>
        <HiddenInput name="pickupPoint" value={pickupPoints[0].id} />
        {pickupPoints[0].name}
      </div>}
    </>}

    {!showPickupPoint && showRedemptionLocation && <>
      {!showReadOnlyRedemptionLocation && <Select
        required
        label="Location"
        noValidationSpacing
        className="full-width"
        name="redemptionLocation"
        onChange={onLocationChange}
        placeholder="Select a location"
        defaultValue={defaultRedemptionLocationId ?? ((redemptionLocation.length === 1) ? redemptionLocation[0].id : undefined)}
      >
        {redemptionLocation.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
      </Select>}
      {showReadOnlyRedemptionLocation && <div className="full-width">
        <HiddenInput name="redemptionLocation" value={redemptionLocation[0].id} />
        {!removeLocationLabel && (<>
          <TicketOptionsTitle variant="medium" weight="bold">Location</TicketOptionsTitle>
          {redemptionLocation[0].name}
        </>)}
      </div>}
    </>}

    {experienceView.originalExperience.ticketModes && (
      <Select
        required
        label="Ticket options"
        noValidationSpacing
        name="ticketMode"
        onChange={onTicketModeChange}
        placeholder="Select an option"
        defaultValue={defaultTicketMode}
      >
        {experienceView.originalExperience.ticketModes.map(ticketMode => <option key={ticketMode.key} value={ticketMode.key}>{ticketMode.text}</option>)}
      </Select>
    )}

    {!experienceView.originalExperience.hasCalendar && selectedDate &&
      <input type="hidden" name="date" value={selectedDate} />}
    {experienceView.originalExperience.hasCalendar && !!allDates?.length && <ExperienceDateSelect
      availableDates={availableDates}
      selectedDate={selectedDate}
      onDateChange={onDateSelect}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      noValidationSpacing
      name="date"
      label="Date"
      required
    />}

    {languages.length > 1 && <Select
      label="Language"
      name="language"
      placeholder="Select a language"
      defaultValue={defaultLanguageId ?? (languages.length === 1 ? languages[0].name : undefined)}
      required
      noValidationSpacing
      onChange={onLanguageChange}
    >
      {languages.map(lang => <option key={lang.id} value={lang.id}>{lang.name}</option>)}
    </Select>}
    {languages.length === 1 && <div>
      <HiddenInput name="language" value={languages[0].id} />
      <BodyText variant="medium">Language</BodyText>
      <UniqueRegisterContainer>
        <BodyText variant="large">{languages[0].name}</BodyText>
      </UniqueRegisterContainer>
    </div>}

    {showTimeSlots && <div style={{ alignSelf: 'flex-end' }}>
      {uniqueTimeSlots.length > 1 && <Select
        label="Time"
        name="time"
        placeholder="Select a time"
        data-testid="experience-time-select"
        onChange={onTimeChange}
        defaultValue={defaultTimeSlot}
        required
        noValidationSpacing
        value={selectedTimeSlot}
      >
        {uniqueTimeSlots.map(slot => <option key={slot} value={slot}>
          {slot}
        </option>)}
      </Select>}
      {uniqueTimeSlots.length === 1 && <>
        <HiddenInput name="time" value={uniqueTimeSlots[0]} />
        <BodyText variant="medium">Time</BodyText>
        <UniqueRegisterContainer>
          <BodyText variant="large">Only available at {uniqueTimeSlots[0]}</BodyText>
        </UniqueRegisterContainer>
      </>}
    </div>}

    {!showTimeSlots && uniqueTimeSlots.length > 0 && (
      <HiddenInput name="time" value={uniqueTimeSlots[0]} />
    )}

  </BookingDateTimeContainer>
  )
}

export default ExperienceTicketInputs
